import {GetAsync, observer_mgl} from "mobx-graphlink";
import React from "react";
import {observable, runInAction} from "mobx";
import {terrainQuadtree_current, terrainWorkerPool_current, terrainPreparedData_current} from "../../../Terrain/Heights/TerrainRenderer.js";
import {terrainProcessStats} from "../../../Terrain/Heights/TerrainProcessStats.js";
import {Button, Text, CheckBox, Row, Spinner} from "react-vcomponents";
import {RunInAction_Set} from "web-vcore";
import {ResolveObjPosInWorld} from "../../../Server/DB/mapObjects/$mapObject.js";
import {MapState} from "../../../Store/main/maps/@MapState.js";
import {GetMapObject, GetMapObjects} from "../../../Server/DB/mapObjects.js";
import {DMap} from "../../../Server/DB/maps/@Map.js";
import {colors} from "../../../Utils/UI/GlobalStyles.js";
import {ToolDetailsUI} from "../MapUI/Toolbox/ToolDetailsUI.js";
import {zIndexes} from "../../../Utils/UI/ZIndexes.js";
import {Timer} from "js-vextensions";

// 2026-08-02 — macro-grid elevation probe for the "m-elev[X]" readout below.
// `terrainWorkerPool_current.dispatchToWorker(0, "sampleMacroH", {cacheKey, x, y})` replies
// asynchronously with `evalH(x, y, macro.grid)` from worker 0.  We stash the latest reply in
// this module-level observable box so `observer_mgl` rerenders the MenuBar when it lands.  The
// dispatch is epsilon-throttled (only re-fired when the mouse moves > 1m from the last sample
// point) so panning the camera with the mouse stationary doesn't spam the worker.
const macroElevProbe = observable.box<number | null>(null);
let macroElevProbe_lastX = NaN, macroElevProbe_lastY = NaN;
let macroElevProbe_inFlight = false;
function maybeDispatchMacroElevProbe(x: number, y: number): void {
	// Throttle: skip if the mouse hasn't moved > 1m since the last dispatch, OR a dispatch is
	// already in flight (the reply will land and trigger a re-render; we'll re-evaluate then).
	if (macroElevProbe_inFlight) return;
	if (Math.abs(x - macroElevProbe_lastX) < 1 && Math.abs(y - macroElevProbe_lastY) < 1) return;
	// Pick the first prepared token's cacheKey.  In multi-mod maps there's one entry per mod;
	// the macro grid is per-cacheKey so this reads the first mod's macro field — sufficient for
	// the diagnostic intent (the user is investigating a single-lake map).
	const prepared = terrainPreparedData_current.values().next().value as
		| {cacheKey: string; worldBBox?: {minX: number; minY: number; maxX: number; maxY: number}}
		| undefined;
	if (!prepared?.cacheKey) return;
	const wp = terrainWorkerPool_current;
	if (!wp) return;

	macroElevProbe_lastX = x;
	macroElevProbe_lastY = y;
	macroElevProbe_inFlight = true;
	// give up and reset in-flight flag, if we don't get a reply within 1s
	const giveUpTimer = new Timer(1000, ()=>{
		runInAction(()=>macroElevProbe_inFlight = false);
	}, 1).Start();

	wp.dispatchToWorker(0, {type: "sampleMacroH", cacheKey: prepared.cacheKey, x, y}, [], resp=>{
		giveUpTimer.Stop();
		macroElevProbe_inFlight = false;
		const h = resp?.h as number | null | undefined;
		runInAction(()=>macroElevProbe.set(h ?? null));
	});
}

export const MenuBar = observer_mgl(({map, mapState}: {map: DMap, mapState: MapState})=>{
	// catch loading-bails and replace with "undefined", so menu-bar doesn't flicker on object-creation (note: add-object button below checks for undefined specifically)
	const refObj = GetMapObject.CatchBail(undefined, mapState.referenceObject, map.id);
	const refObjPos = refObj ? ResolveObjPosInWorld.CatchBail(undefined, refObj) : null;
	const activeObj = GetMapObject.CatchBail(undefined, mapState.activeObject, map.id);
	const activeObjPos = activeObj ? ResolveObjPosInWorld.CatchBail(undefined, activeObj) : null;

	const mousePos_world = mapState.mousePos_world_0ms;
	//const mousePos_fromRef = mousePos_world && refObjPos ? mousePos_world.Minus(refObjPos) : null;
	const mousePos_fromActive = mousePos_world && activeObjPos ? mousePos_world.Minus(activeObjPos) : null;
	const mousePos_elev = mousePos_world && terrainQuadtree_current ? terrainQuadtree_current.sampleHeightAt(mousePos_world.x, mousePos_world.y) : null;
	// 2026-08-02 — macro-grid probe.  Fire-and-forget a `sampleMacroH` dispatch to worker 0
	// whenever the mouse moves; the reply updates the module-level `macroElevProbe` observable
	// box, which the `observer_mgl` wrapper rerenders on.  Reading `.get()` here subscribes
	// this render to the box → the readout updates when the reply lands (typically <5ms).
	const mousePos_macroElev = (()=>{
		if (!mousePos_world) return null;
		maybeDispatchMacroElevProbe(mousePos_world.x, mousePos_world.y);
		return macroElevProbe.get();
	})();
	const mousePos_str = [
		mousePos_world ? `world[${mousePos_world.x.toFixed(1)},${mousePos_world.y.toFixed(1)}]` : null,
		//mousePos_fromRef ? `ref[${mousePos_fromRef.x.toFixed(1)},${mousePos_fromRef.y.toFixed(1)}]` : null,
		mousePos_fromActive ? `active[${mousePos_fromActive.x.toFixed(1)},${mousePos_fromActive.y.toFixed(1)}]` : null,
		mousePos_macroElev != null ? `m-elev[${mousePos_macroElev.toFixed(1)}m]` : null,
		mousePos_elev != null ? `elev[${mousePos_elev.toFixed(1)}m]` : null,
	].filter(a=>a).join(" ");

	return (
		<Row style={{flexWrap: "wrap", height: 25, /*minHeight: 25, maxHeight: 50, background: colors.mapUI_panels*/}}>
			<ToolDetailsUI {...{map, mapState}}/>
			<Row ml="auto" style={{height: 25, zIndex: zIndexes.actionBar, background: colors.mapUI_panels}}>
				{/* Set13 — async-shader-compile progress: while the terrain material programs
				    compile (`compileAsync`), show a small label left of the 3D button.  Reads the
				    observable terrainProcessStats fields, so this `observer_mgl` rerenders as the
				    count climbs and disappears once the wave drains (pending → null). */}
				{terrainProcessStats.materialCompilesPending != null && terrainProcessStats.materialCompilesPending > 0 && (
					<Text sel style={{fontSize: 10, marginRight: 5}}>
						Compiling shaders ({terrainProcessStats.materialCompilesTotal - terrainProcessStats.materialCompilesPending}/{terrainProcessStats.materialCompilesTotal})
					</Text>
				)}
				<Button text={mapState.settings.view3D.view3D_enabled ? "3D: ON" : "3D: OFF"}
					title={mapState.settings.view3D.view3D_enabled
						? "Exit 3D mode (return to 2D authoring)."
						: "Enter 3D mode (locked viewpoint)."}
					onClick={()=>RunInAction_Set(()=>{
						const view3D = mapState.settings.view3D;
						view3D.view3D_enabled = !view3D.view3D_enabled;
						// When turning ON, auto-select the active viewpoint so the mode just works:
						// prefer the currently-selected object if it carries a Viewpoint, else fall
						// back to the (single) viewpoint object on the map. If neither, the mode
						// still enables and the surface shows the "place a viewpoint" hint.
						if (view3D.view3D_enabled) {
							const viewpointObjects = GetMapObjects(map.id).filter(o=>(o.modifiers ?? []).some(m=>m.enabled && m.viewpoint));
							const selectedHasViewpoint = mapState.activeObject != null
								&& viewpointObjects.some(o=>o.id == mapState.activeObject);
							const target = selectedHasViewpoint
								? mapState.activeObject
								: viewpointObjects.length == 1 ? viewpointObjects[0].id : null;
							if (target) view3D.view3D_activeViewpointObjID = target;
						}
					})}/>
				{mousePos_str.length > 0 && <>
					<Text ml={5} sel style={{fontSize: 12}}>Mouse: {mousePos_str}</Text>
				</>}
				<CheckBox ml={5} text="Snap, pos:" value={mapState.snap_pos_enabled} onChange={val=>RunInAction_Set(()=>mapState.snap_pos_enabled = val)}/>
				<Spinner ml={5} step="any" min={0} style={{width: 60}} value={mapState.snap_pos_step} onChange={val=>RunInAction_Set(()=>mapState.snap_pos_step = val)}/>
				{/*<CheckBox ml={5} text="Rot:" value={mapState.snap_rot_enabled} onChange={val=>RunInAction_Set(()=>mapState.snap_rot_enabled = val)}/>*/}
			</Row>
		</Row>
	);
});